///
/// EarthBrowser - Query Builder Utilities
///
/// Helper functions for building and manipulating queries
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type {
	DataQuery,
	DataValue,
	FilterGroup,
	FilterCondition,
	SortSpec,
	LogicalOperator,
	FilterOperator,
} from './types'

/**
 * Create an empty query
 */
export function createQuery(): DataQuery {
	return {}
}

/**
 * Create a filter group
 */
export function createFilterGroup(
	operator: LogicalOperator = 'AND',
	conditions: (FilterCondition | FilterGroup)[] = []
): FilterGroup {
	return { operator, conditions }
}

/**
 * Create a filter condition
 */
export function createFilterCondition(
	column: string,
	operator: FilterOperator,
	value?: DataValue | DataValue[],
	caseSensitive = false
): FilterCondition {
	return { column, operator, value, caseSensitive }
}

/**
 * Add a filter to a query
 */
export function addFilter(
	query: DataQuery,
	condition: FilterCondition | FilterGroup
): DataQuery {
	if (!query.filters) {
		query.filters = createFilterGroup('AND', [condition])
	} else {
		query.filters.conditions.push(condition)
	}
	return query
}

/**
 * Set sorting on a query
 */
export function setSort(query: DataQuery, sort: SortSpec[]): DataQuery {
	return { ...query, sort }
}

/**
 * Set pagination on a query
 */
export function setPagination(
	query: DataQuery,
	offset: number,
	limit: number
): DataQuery {
	return { ...query, offset, limit }
}

/**
 * Set search on a query
 */
export function setSearch(
	query: DataQuery,
	searchQuery: string,
	columns?: string[]
): DataQuery {
	if (!searchQuery) {
		const rest = { ...query }
		delete rest.search
		return rest
	}
	return { ...query, search: { query: searchQuery, columns } }
}

/**
 * Set column selection on a query
 */
export function setColumns(query: DataQuery, columns: string[]): DataQuery {
	return { ...query, columns }
}

/**
 * Clear filters from a query
 */
export function clearFilters(query: DataQuery): DataQuery {
	const rest = { ...query }
	delete rest.filters
	return rest
}

/**
 * Clear sorting from a query
 */
export function clearSort(query: DataQuery): DataQuery {
	const rest = { ...query }
	delete rest.sort
	return rest
}

/**
 * Clear pagination from a query
 */
export function clearPagination(query: DataQuery): DataQuery {
	const rest = { ...query }
	delete rest.offset
	delete rest.limit
	return rest
}

/**
 * Reset query to empty state
 */
export function resetQuery(): DataQuery {
	return {}
}

/**
 * Merge two queries (second query takes precedence)
 */
export function mergeQueries(base: DataQuery, override: DataQuery): DataQuery {
	return { ...base, ...override }
}

/**
 * Clone a query
 */
export function cloneQuery(query: DataQuery): DataQuery {
	return JSON.parse(JSON.stringify(query)) as DataQuery
}

/**
 * Quick filter builders for common cases
 */
export const QuickFilters = {
	equals: (column: string, value: DataValue) =>
		createFilterCondition(column, 'equals', value),

	notEquals: (column: string, value: DataValue) =>
		createFilterCondition(column, 'not_equals', value),

	contains: (column: string, value: string, caseSensitive = false) =>
		createFilterCondition(column, 'contains', value, caseSensitive),

	startsWith: (column: string, value: string, caseSensitive = false) =>
		createFilterCondition(column, 'starts_with', value, caseSensitive),

	endsWith: (column: string, value: string, caseSensitive = false) =>
		createFilterCondition(column, 'ends_with', value, caseSensitive),

	greaterThan: (column: string, value: number | Date) =>
		createFilterCondition(column, 'greater_than', value),

	lessThan: (column: string, value: number | Date) =>
		createFilterCondition(column, 'less_than', value),

	between: (column: string, min: number | Date, max: number | Date) =>
		createFilterCondition(column, 'between', [min, max]),

	isNull: (column: string) =>
		createFilterCondition(column, 'is_null'),

	isNotNull: (column: string) =>
		createFilterCondition(column, 'is_not_null'),

	in: (column: string, values: DataValue[]) =>
		createFilterCondition(column, 'in', values),

	regex: (column: string, pattern: string, caseSensitive = false) =>
		createFilterCondition(column, 'regex', pattern, caseSensitive),
}

/**
 * Quick sort builders
 */
export const QuickSort = {
	ascending: (column: string): SortSpec => ({ column, direction: 'asc' }),
	descending: (column: string): SortSpec => ({ column, direction: 'desc' }),
}

/**
 * Validate a query
 */
export function validateQuery(query: DataQuery): { valid: boolean; errors: string[] } {
	const errors: string[] = []

	// Validate pagination
	if (query.offset !== undefined && query.offset < 0) {
		errors.push('Offset must be >= 0')
	}

	if (query.limit !== undefined && query.limit < 0) {
		errors.push('Limit must be >= 0')
	}

	// Validate filters
	if (query.filters) {
		validateFilterGroup(query.filters, errors)
	}

	return {
		valid: errors.length === 0,
		errors,
	}
}

function validateFilterGroup(group: FilterGroup, errors: string[]): void {
	if (group.conditions.length === 0) {
		errors.push('Filter group must have at least one condition')
	}

	group.conditions.forEach((condition, index) => {
		if ('operator' in condition && (condition.operator === 'AND' || condition.operator === 'OR')) {
			validateFilterGroup(condition, errors)
		} else {
			validateFilterCondition(condition as FilterCondition, index, errors)
		}
	})
}

function validateFilterCondition(
	condition: FilterCondition,
	index: number,
	errors: string[]
): void {
	if (!condition.column) {
		errors.push(`Condition ${index}: column is required`)
	}

	if (!condition.operator) {
		errors.push(`Condition ${index}: operator is required`)
	}

	// Validate value requirements
	const requiresValue: FilterOperator[] = [
		'equals',
		'not_equals',
		'contains',
		'not_contains',
		'starts_with',
		'ends_with',
		'greater_than',
		'greater_than_or_equal',
		'less_than',
		'less_than_or_equal',
		'in',
		'not_in',
		'between',
		'regex',
	]

	if (requiresValue.includes(condition.operator) && condition.value === undefined) {
		errors.push(`Condition ${index}: value is required for operator '${condition.operator}'`)
	}

	// Validate array values
	if (condition.operator === 'in' || condition.operator === 'not_in') {
		if (!Array.isArray(condition.value)) {
			errors.push(`Condition ${index}: value must be an array for operator '${condition.operator}'`)
		}
	}

	if (condition.operator === 'between') {
		if (!Array.isArray(condition.value) || condition.value.length !== 2) {
			errors.push(`Condition ${index}: value must be an array of 2 elements for operator 'between'`)
		}
	}
}

